import React from 'react';
import PropTypes from 'prop-types';
import classNames from '../../../utils/classNames';
import { MAX_POST_ATTACHMENTS } from '../../../constants/posts';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import './PostAttachmentsPicker.scss';

const PostAttachmentsPicker = ({
    attachments,
    onRemoveAttachment,
    maxAttachments,
    error,
    disabled,
}) => {
    const total = Array.isArray(attachments) ? attachments.length : 0;

    const renderAttachment = (attachment) => {
        const isPending = attachment?.source === 'local';
        const remoteUrl = attachment?.metadata?.url ? resolveMediaUrl(attachment.metadata.url) : '';
        const src = attachment?.previewUrl || remoteUrl;
        const alt = isPending ? 'Pending attachment preview' : 'Post attachment image';
        return (
            <figure
                key={attachment.id}
                className={classNames('post-attachments-picker__item', {
                    'post-attachments-picker__item--pending': isPending,
                })}
            >
                <button
                    type="button"
                    className="post-attachments-picker__remove"
                    aria-label="Remove attachment"
                    onClick={() => onRemoveAttachment?.(attachment.id)}
                    disabled={disabled}
                >
                    <span aria-hidden="true">×</span>
                </button>
                {src ? (
                    <img src={src} alt={alt} className="post-attachments-picker__image" />
                ) : (
                    <div className="post-attachments-picker__placeholder" aria-hidden="true">
                        <span>Image preview unavailable</span>
                    </div>
                )}
                {isPending && (
                    <figcaption className="post-attachments-picker__badge">Pending upload</figcaption>
                )}
            </figure>
        );
    };

    if (!total && !error) {
        return null;
    }

    return (
        <div className="post-attachments-picker">
            {total > 0 && (
                <div className="post-attachments-picker__header">
                    <span className="post-attachments-picker__count" aria-live="polite">
                        {total} / {maxAttachments}
                    </span>
                </div>
            )}
            {error && (
                <p className="post-attachments-picker__error" role="alert">
                    {error}
                </p>
            )}
            {total > 0 && (
                <div className="post-attachments-picker__grid">
                    {attachments.map(renderAttachment)}
                </div>
            )}
        </div>
    );
};

PostAttachmentsPicker.propTypes = {
    attachments: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        previewUrl: PropTypes.string,
        source: PropTypes.oneOf(['local', 'remote']).isRequired,
        metadata: PropTypes.shape({
            url: PropTypes.string,
            contentType: PropTypes.string,
        }),
    })).isRequired,
    onRemoveAttachment: PropTypes.func.isRequired,
    maxAttachments: PropTypes.number,
    error: PropTypes.string,
    disabled: PropTypes.bool,
};

PostAttachmentsPicker.defaultProps = {
    maxAttachments: MAX_POST_ATTACHMENTS,
    error: null,
    disabled: false,
};

export default PostAttachmentsPicker;
